import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { autoInputIxdFromDsd } from "@/apis/compare/route";
import { isInvalidIxdStructureError } from "@/apis/errors";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import { pushModal } from "@/stores/modal";
import { downloadBlob } from "@/utils/download-blob";
import { FileUploadSection } from "@/components/file-upload-section";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import { Button } from "@/toggle-ui-kit/button";

export const IxdAutoInputPage = () => {
  const navigate = useNavigate();
  const [priorDsd, setPriorDsd] = useState<File | null>(null);
  const [priorIxd, setPriorIxd] = useState<File | null>(null);
  const [currentDsd, setCurrentDsd] = useState<File | null>(null);
  const [currentIxd, setCurrentIxd] = useState<File | null>(null);

  const canRun = !!(priorDsd && priorIxd && currentDsd && currentIxd);

  const mutation = useMutation({
    mutationFn: () => {
      if (!priorDsd || !priorIxd || !currentDsd || !currentIxd) {
        throw new Error("4개 파일을 모두 업로드해주세요.");
      }
      return autoInputIxdFromDsd(priorDsd, priorIxd, currentDsd, currentIxd);
    },
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename);
      toast.success("자동 기입이 완료되어 ZIP을 다운로드합니다.");
    },
    onError: (error: unknown) => {
      if (isInvalidIxdStructureError(error)) {
        pushModal(UploadFailedModal);
        return;
      }
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (error as Error)?.message ??
        "요청 처리 중 오류가 발생했습니다.";
      toast.error(detail);
    },
  });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-neutral-100">
      <header className="flex h-14 px-6 items-center gap-4 border-b border-neutral-200 bg-white/80 backdrop-blur-sm">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-neutral-950">
          IXD 자동 기입 (DSD → IXD)
        </h1>
      </header>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-neutral-900 mb-3">
              전기/당기 DSD·IXD를 업로드하세요
            </h2>
            <p className="text-neutral-600 max-w-lg mx-auto">
              4개 파일을 기반으로 당기 IXD에 값을 자동 기입하여 ZIP으로
              다운로드합니다.
            </p>
          </div>

          <section className="mb-6">
            <h3 className="text-sm font-semibold text-neutral-700 mb-3">
              전기 (Prior)
            </h3>
            <div className="flex gap-6">
              <FileUploadSection
                label="DSD 파일"
                accept=".dsd"
                onFileSelect={setPriorDsd}
                isLoading={false}
                fileName={priorDsd?.name}
                description=".dsd 형식"
                accentColor="blue"
              />
              <FileUploadSection
                label="IXD 파일"
                accept=".ixd"
                onFileSelect={setPriorIxd}
                isLoading={false}
                fileName={priorIxd?.name}
                description=".ixd 형식"
                accentColor="blue"
              />
            </div>
          </section>

          <section className="mb-8">
            <h3 className="text-sm font-semibold text-neutral-700 mb-3">
              당기 (Current)
            </h3>
            <div className="flex gap-6">
              <FileUploadSection
                label="DSD 파일"
                accept=".dsd"
                onFileSelect={setCurrentDsd}
                isLoading={false}
                fileName={currentDsd?.name}
                description=".dsd 형식"
                accentColor="purple"
              />
              <FileUploadSection
                label="IXD 파일"
                accept=".ixd"
                onFileSelect={setCurrentIxd}
                isLoading={false}
                fileName={currentIxd?.name}
                description=".ixd 형식"
                accentColor="purple"
              />
            </div>
          </section>

          <IxdUploadNotice accepts=".dsd, .ixd" className="mb-8" />

          <div className="flex justify-center">
            <Button
              variant="solid"
              color="secondary"
              size="xlarge"
              disabled={!canRun || mutation.isPending}
              onClick={() => mutation.mutate()}
              leftIcon={
                mutation.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : undefined
              }
              className="px-12"
            >
              {mutation.isPending ? "처리 중..." : "자동 기입 실행"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IxdAutoInputPage;
